import { useState, useEffect, useCallback } from "react";
import { Beer, Check, Loader2, ExternalLink, ChevronsUpDown, Lock } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { BJCP_STYLES } from "../lib/bjcpStyles";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";

const APPS_SCRIPT_URL = process.env.REACT_APP_APPS_SCRIPT_URL;
const EMAILJS_API_URL = process.env.REACT_APP_EMAILJS_API_URL;
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_USER_ID = process.env.REACT_APP_EMAILJS_USER_ID;

// --- COMPONENTE INTERNO PER IL COUNTDOWN ISCRIZIONI ---
const RegistrationTimer = () => {
  const deadline = new Date("2026-10-31T23:59:59").getTime();
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = deadline - now;
      if (distance > 0) {
        setTimeLeft({
          days: Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000),
        });
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [deadline]);

  return (
    <div className="mb-8 p-6 border border-zinc-800 bg-black/40 text-center">
      <span className="uppercase tracking-[0.4em] text-[10px] font-black text-[#c49a45] block mb-4">
        CHIUSURA ISCRIZIONI: 31 OTTOBRE
      </span>
      <div className="flex justify-center gap-6">
        <div className="flex flex-col">
          <span className="bb-display text-3xl text-white leading-none">{timeLeft.days}</span>
          <span className="text-[8px] uppercase tracking-widest text-zinc-600 mt-2">Giorni</span>
        </div>
        <div className="flex flex-col">
          <span className="bb-display text-3xl text-white leading-none">{timeLeft.hours}</span>
          <span className="text-[8px] uppercase tracking-widest text-zinc-600 mt-2">Ore</span>
        </div>
        <div className="flex flex-col">
          <span className="bb-display text-3xl text-white leading-none">{timeLeft.minutes}</span>
          <span className="text-[8px] uppercase tracking-widest text-zinc-600 mt-2">Min</span>
        </div>
        <div className="flex flex-col">
          <span className="bb-display text-3xl text-white leading-none">{timeLeft.seconds}</span>
          <span className="text-[8px] uppercase tracking-widest text-zinc-600 mt-2">Sec</span>
        </div>
      </div>
    </div>
  );
};

// --- FUNZIONE EMAIL AGGIORNATA ---
const sendRegistrationEmail = async (data, regId) => {
  const templateParams = {
    to_name: data.brewer_name,
    email: data.email,
    registration_id: regId,
    beer_style: data.beer_style
  };

  try {
    const response = await fetch(EMAILJS_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        service_id: EMAILJS_SERVICE_ID,
        template_id: EMAILJS_TEMPLATE_ID,
        user_id: EMAILJS_USER_ID,
        template_params: templateParams
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('EmailJS Error Details:', errorText);
    }
  } catch (error) {
    console.error('Errore invio mail:', error);
  }
};

export default function RegistrationSection() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(null); 
  const [styleOpen, setStyleOpen] = useState(false);
  const [availability, setAvailability] = useState({ max: 40, remaining: 40, sold_out: false }); 
  const [form, setForm] = useState({
    brewer_name: "", email: "", phone: "", beer_style: "", abv: "", description: ""
  });

  const loadAvailability = useCallback(async () => {
    try {
      const { data } = await api.get("/registrations/availability");
      setAvailability(data);
    } catch (e) { }
  }, []);

  useEffect(() => { loadAvailability(); }, [loadAvailability]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const finalStyle = form.beer_style || "DA DEFINIRE (comunicherò lo stile in seguito)";
    setLoading(true);
    
    try {
      const payload = {
        ...form,
        beer_style: finalStyle,
        beer_name: `Birra di ${form.brewer_name}`,
      };
      
      const { data } = await api.post("/registrations", payload);
      const iscrittiAttuali = availability.max - availability.remaining;
      const numeroProgressivo = (iscrittiAttuali + 1).toString().padStart(2, '0');

      setConfirmed({ ...data, progressivo: numeroProgressivo });
      sendRegistrationEmail({ ...form, beer_style: finalStyle }, numeroProgressivo);

      fetch(APPS_SCRIPT_URL, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "register",
          id_etichetta: numeroProgressivo,
          nome: form.brewer_name,
          email: form.email,
          telefono: form.phone,
          stile: finalStyle,
          abv: form.abv,
          descrizione: form.description
        })
      });

      window.open(data.paypal_url, "_blank", "noopener,noreferrer");
      toast.success("Iscrizione registrata. Controlla la tua mail!");
      loadAvailability();
    } catch (err) {
      toast.error("Errore durante l'iscrizione.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPaid = async () => {
    if (!confirmed) return;
    try {
      await api.post(`/registrations/${confirmed.id}/confirm-payment`);
      fetch(APPS_SCRIPT_URL, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "confirm", id_etichetta: confirmed.progressivo })
      });

      toast.success("Pagamento confermato!");
      setOpen(false);
      setConfirmed(null);
      setForm({ brewer_name: "", email: "", phone: "", beer_style: "", abv: "", description: "" });
    } catch (err) {
      toast.error("Errore conferma");
    }
  };

  const grouped = BJCP_STYLES.reduce((acc, s) => {
    (acc[s.category] = acc[s.category] || []).push(s);
    return acc;
  }, {});

  const benefits = [
    "Iscrizione 1 birra: (min. 2 bottiglie da 33cl).",
    "Scheda BJCP: analisi e punteggi inviati a fine evento.",
    "Podio e Premi: in palio per i primi 3 classificati.",
    "Vivi l'evento: partecipa di persona e goditi la sfida dal vivo.",
    "Hall of Fame: ingresso ufficiale nello storico del Beer Battle.",
  ];

  return (
    <section id="iscrizione" className="bb-section relative bg-black py-24 px-4">
      <div className="bb-container relative z-10">
        <div className="text-center mb-12">
          <span className="uppercase tracking-[0.3em] text-[11px] font-bold block mb-4" style={{ color: "var(--bb-primary)" }}>Iscrizione 2026</span>          
          <h2 className="bb-display text-6xl md:text-8xl text-white leading-none uppercase">
            ENTRA NELL'<span style={{ color: "var(--bb-primary)" }}>ARENA</span>
          </h2>
        </div>

        <div className="max-w-3xl mx-auto">
          <div className="border-2 p-8 md:p-16 relative" style={{ borderColor: "var(--bb-primary)", background: "var(--bb-surface)" }}>
            {!confirmed ? (
              <>
                <div className="text-center mb-10">
                  <div className="flex items-baseline justify-center gap-3">
                    <span className="bb-display text-8xl md:text-9xl" style={{ color: "var(--bb-primary)" }}>10</span>
                    <span className="bb-display text-4xl text-zinc-300">€</span>
                  </div>
                  <p className="text-zinc-500 uppercase tracking-widest text-[10px] mt-2">Quota di iscrizione per birra</p>
                </div>

                <div className="h-px bg-zinc-800/50 w-full mb-12" />

                <ul className="space-y-5 mb-16 max-w-md mx-auto text-left">
                  {benefits.map((b) => (
                    <li key={b} className="flex items-start gap-4 text-zinc-200">
                      <Check size={18} style={{ color: "var(--bb-primary)" }} className="mt-1 flex-shrink-0" />
                      <span className="text-sm md:text-base font-medium opacity-90">{b}</span>
                    </li>
                  ))}
                </ul>

                <RegistrationTimer />

                <div className="bg-black/40 border border-zinc-800 p-8 mb-8 text-center">
                  <span className="text-zinc-600 uppercase tracking-[0.3em] text-[9px] font-black block mb-4">Posti Occupati</span>
                  <div className="flex items-baseline justify-center gap-3">
                   <span className="bb-display text-6xl text-[#c49a45]">{availability.max - availability.remaining}</span>
                    <span className="bb-display text-3xl text-zinc-700">/ {availability.max}</span>
                  </div>
                </div>

                {!open ? (
                  <button onClick={() => !availability.sold_out && setOpen(true)} disabled={availability.sold_out} className="w-full bg-[#c49a45] text-black font-black py-6 uppercase tracking-[0.3em] hover:bg-white transition-all text-sm">
                    {availability.sold_out ? "ISCRIZIONI CHIUSE" : "ISCRIVI LA TUA BIRRA"}
                  </button>
                ) : (
                  <form onSubmit={handleSubmit} className="mt-10 space-y-6 text-left animate-fadeIn">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Nome Birraio *</label>
                        <input name="brewer_name" required value={form.brewer_name} onChange={handleChange} className="bb-input w-full bg-black border border-zinc-800 p-4 text-white focus:border-[#c49a45] outline-none" placeholder="Nome e Cognome" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Email *</label>
                        <input name="email" type="email" required value={form.email} onChange={handleChange} className="bb-input w-full bg-black border border-zinc-800 p-4 text-white focus:border-[#c49a45] outline-none" placeholder="Email" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Telefono *</label>
                      <input name="phone" required value={form.phone} onChange={handleChange} className="bb-input w-full bg-black border border-zinc-800 p-4 text-white focus:border-[#c49a45] outline-none" placeholder="Numero di telefono" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Stile BJCP 2021 *</label>
                      <Popover open={styleOpen} onOpenChange={setStyleOpen}>
                        <PopoverTrigger asChild>
                          <button type="button" className="w-full bg-black border border-zinc-800 p-4 text-white flex justify-between items-center outline-none focus:border-[#c49a45]">
                            <span className={form.beer_style ? "text-white" : "text-zinc-500"}>{form.beer_style || "Seleziona stile"}</span>
                            <ChevronsUpDown size={18} className="text-zinc-500" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] bg-zinc-900 border-zinc-800">
                          <Command className="bg-transparent">
                            <CommandInput placeholder="Cerca stile..." className="text-white" />
                            <CommandList className="max-h-[250px]">
                              {Object.entries(grouped).map(([cat, styles]) => (
                                <CommandGroup key={cat} heading={cat}><CommandEmpty>Nessuno stile trovato.</CommandEmpty>
                                  {styles.map((s) => (
                                    <CommandItem key={s.code} onSelect={() => { setForm({ ...form, beer_style: `${s.code}. ${s.name}` }); setStyleOpen(false); }} className="cursor-pointer hover:bg-[#c49a45] p-2 text-white">
                                      <span className="font-bold mr-2 text-[#c49a45]">{s.code}</span>{s.name}
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              ))}
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">ABV (Alcohol by Volume) %</label>
                      <input name="abv" value={form.abv} onChange={handleChange} className="bb-input w-full bg-black border border-zinc-800 p-4 text-white focus:border-[#c49a45] outline-none" placeholder="Es. 5.5%" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Descrizione Birra</label>
                      <textarea name="description" value={form.description} onChange={handleChange} className="bb-input w-full bg-black border border-zinc-800 p-4 text-white min-h-[120px] outline-none focus:border-[#c49a45]" placeholder="Racconta la tua birra..." />
                    </div>
                    <button type="submit" disabled={loading} className="w-full bg-[#c49a45] text-black font-black py-6 uppercase tracking-[0.3em] hover:bg-white transition-all flex items-center justify-center gap-3">
                      {loading ? <Loader2 className="animate-spin" /> : <><ExternalLink size={20} /> Conferma e paga 10€</>}
                    </button>
                  </form>
                )}
              </>
            ) : (
              <div className="text-center animate-fadeIn py-10 flex flex-col items-center">
                <Check size={60} className="text-[#c49a45] mb-6" />
                <h3 className="bb-display text-4xl text-white mb-2 leading-none">ISCRIZIONE REGISTRATA!</h3>
                
                {/* ISTRUZIONI SOCIAL CENTRATE */}
                <div className="mt-8 mb-10 space-y-2 px-4 w-full">
                  <p className="text-white text-[13px] uppercase tracking-[0.2em] font-black">
                    📸 FAI UNO SCREENSHOT 9:16 E SFIDA I TUOI COLLEGHI!
                  </p>
                  <p className="text-zinc-500 text-[10px] uppercase tracking-widest">
                    Taggaci per essere ripostato: <span className="text-white">@beer_battle</span> e <span className="text-white">@hilltopbrewery</span>
                  </p>
                </div>

                {/* BADGE FORMATO STORIA 9:16 - BILANCIATO E CENTRATO */}
                <div className="my-8 w-full max-w-[320px] aspect-[9/16] bg-black border-2 border-[#c49a45] p-1 shadow-[0_0_60px_rgba(196,154,69,0.25)] flex flex-col mx-auto overflow-hidden">
                  <div className="flex-1 border border-[#c49a45]/30 flex flex-col justify-between items-center text-center pt-14 pb-14 px-6">
                    
                    {/* TOP: LOGO E CLAIM */}
                    <div className="space-y-4">
                      <div className="bb-display text-3xl tracking-widest text-[#c49a45]">
                        BEER<span className="text-white">BATTLE</span>
                      </div>
                      <p className="text-[#c49a45] text-[10px] tracking-[0.5em] font-black uppercase">
                        È TEMPO DI COMBATTERE
                      </p>
                    </div>

                    {/* CENTER: SFIDA L'ARENA */}
                    <div>
                      <h4 className="bb-display text-6xl text-white leading-[0.9] tracking-tighter italic uppercase">
                        SFIDA<br/>L'ARENA
                      </h4>
                    </div>

                    {/* BOTTOM: DATA E LOCATION */}
                    <div className="w-full space-y-8">
                      <div className="space-y-3">
                        <p className="text-white text-2xl font-black tracking-widest uppercase leading-none">7 NOVEMBRE 2026</p>
                        <p className="text-[#c49a45] text-[10px] tracking-[0.2em] font-bold uppercase italic">
                          SEGNA LA DATA. ENTRA NELLA STORIA.
                        </p>
                      </div>
                      
                      <div className="h-px bg-zinc-900 w-12 mx-auto"></div>

                      <div className="space-y-1 text-zinc-500 text-[10px] tracking-widest uppercase font-bold">
                        <p>Hilltop Brewery</p>
                        <p>Bassano Romano</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6 pt-8 w-full">
                  <div className="flex flex-col gap-3 max-w-sm mx-auto">
                    <a href={confirmed.paypal_url} target="_blank" rel="noopener noreferrer" className="w-full bg-[#c49a45] text-black font-black py-5 block uppercase tracking-[0.2em] text-sm hover:bg-white transition-all">
                      Vai a PayPal
                    </a>
                    <button onClick={handleConfirmPaid} className="w-full border border-zinc-800 text-zinc-500 py-4 uppercase text-[10px] tracking-[0.2em] hover:text-white transition-colors mt-2">
                      Ho già pagato
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}